import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import ManufacturerLogos from '@/components/ManufacturerLogos';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, MapPin, Mail } from 'lucide-react';
import { realDroneInventory } from '@/data/realDroneInventory';
import { formatPrice } from '@/utils/priceFormatter';
import { toast } from 'sonner';
import Footer from '@/components/sections/Footer';

export default function DroneDetailPage() {
  const { droneId } = useParams();
  const navigate = useNavigate();
  const drone = realDroneInventory.find((d) => d.id === parseInt(droneId));

  const [contactForm, setContactForm] = useState({
    name: '',
    company: '',
    email: '',
    message: '',
    interest: '',
    user_type: '',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const interestOptions = [
    'Inspection',
    'Surveying',
    'Cinematography',
    'Security',
    'Agriculture',
    'Search & Rescue',
  ];

  const userTypeOptions = [
    'Commercial Operator (Freelance/SME)',
    'Enterprise Buyer (Corporate)',
    'Government / Public Safety',
    'Dealer / Reseller',
  ];

  if (!drone) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">Drone Not Found</h1>
          <Button onClick={() => navigate('/marketplace')}>Browse Marketplace</Button>
        </div>
      </div>
    );
  }

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact-seller`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          company: contactForm.company,
          message: contactForm.message,
          interest: contactForm.interest,
          user_type: contactForm.user_type,
          drone_model: drone.model,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(
          `Success! Your inquiry has been sent to the seller. ${drone.vendor} will contact you shortly.`
        );
        setDialogOpen(false);
        setContactForm({ name: '', company: '', email: '', message: '', interest: '', user_type: '' });
      } else {
        toast.error('Failed to send inquiry. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <ManufacturerLogos />

      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <Button variant="ghost" className="mb-6" onClick={() => navigate('/marketplace')}>
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Marketplace
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {/* Image */}
            <div>
              <div className="relative aspect-square rounded-lg overflow-hidden bg-muted mb-4">
                <img
                  src={drone.image}
                  alt={drone.model}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 flex gap-2">
                  {drone.featured && (
                    <Badge className="bg-accent text-accent-foreground">Featured</Badge>
                  )}
                  <Badge variant="secondary">{drone.condition}</Badge>
                </div>
              </div>

              {/* Additional Info */}
              <Card>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Category</div>
                      <div className="font-semibold">{drone.category}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Brand</div>
                      <div className="font-semibold">{drone.brand}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span>{drone.location}</span>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Seller</div>
                      <div className="font-semibold">{drone.vendor}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Details */}
            <div>
              <h1 className="text-4xl font-bold mb-2">{drone.model}</h1>
              <div className="text-3xl font-bold text-primary mb-6">
                {formatPrice(drone.price, drone.currency)}
              </div>

              <p className="text-lg text-muted-foreground mb-8">{drone.description}</p>

              {/* CTA */}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="w-full sm:w-auto mb-8">
                    <Mail className="mr-2 w-5 h-5" />
                    Request Quote / Contact Seller
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Contact Seller</DialogTitle>
                    <DialogDescription>
                      Send an inquiry to {drone.vendor} about this {drone.model}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        value={contactForm.company}
                        onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="interest">Primary Application *</Label>
                      <Select
                        required
                        value={contactForm.interest}
                        onValueChange={(value) => setContactForm({ ...contactForm, interest: value })}
                      >
                        <SelectTrigger id="interest">
                          <SelectValue placeholder="Select application" />
                        </SelectTrigger>
                        <SelectContent>
                          {interestOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="user_type">Organization Type *</Label>
                      <Select
                        required
                        value={contactForm.user_type}
                        onValueChange={(value) => setContactForm({ ...contactForm, user_type: value })}
                      >
                        <SelectTrigger id="user_type">
                          <SelectValue placeholder="Select organization type" />
                        </SelectTrigger>
                        <SelectContent>
                          {userTypeOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        required
                        rows={4}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        placeholder="Tell the seller about your needs..."
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? 'Sending...' : 'Send Inquiry'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Specifications */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">Specifications</h2>
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-muted-foreground">Flight Time</div>
                      <div className="font-semibold">{drone.specs.flightTime}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-muted-foreground">Max Speed</div>
                      <div className="font-semibold">{drone.specs.maxSpeed}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-muted-foreground">Payload</div>
                      <div className="font-semibold">{drone.specs.payload}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-muted-foreground">Camera</div>
                      <div className="font-semibold">{drone.specs.camera}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-muted-foreground">Range</div>
                      <div className="font-semibold">{drone.specs.range}</div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Features */}
              <div>
                <h2 className="text-2xl font-bold mb-4">Features</h2>
                <div className="flex flex-wrap gap-2">
                  {drone.features.map((feature) => (
                    <Badge key={feature} variant="outline" className="text-sm px-3 py-1">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}